import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import {
  Alert,
  Box,
  Button,
  IconButton,
  Paper,
  Typography,
} from "@mui/material";
import DirectionsRunIcon from "@mui/icons-material/DirectionsRun";

export default function FirstPersonView({
  cameraRef,
  rendererRef,
  controlsRef,
  boardSizeRef,
  activeRef,
  scenarioKey,
  gridSize = 50,
  eyeHeight = 85,
  moveSpeed = 150,
  mouseSensitivity = 0.002,
}) {
  const [active, setActive] = useState(false);
  const [error, setError] = useState("");
  const enterRef = useRef(null);

  useEffect(() => {
    setActive(false);
    const camera = cameraRef.current;
    const canvas = rendererRef.current?.domElement;
    const orbit = controlsRef.current;
    if (!camera || !canvas || !orbit) return;
    const doc = canvas.ownerDocument;
    const win = doc.defaultView;
    const keys = new Set();
    const view = new THREE.Euler(0, 0, 0, "YXZ");
    const forward = new THREE.Vector3();
    const right = new THREE.Vector3();
    const motion = new THREE.Vector3();
    let saved = null;
    let walking = false;
    let disposed = false;
    let pending = false;
    let frame;
    let lastTime;

    function clampToFloor() {
      const { dimX, dimZ } = boardSizeRef.current;
      const halfWidth = (dimX * gridSize) / 2;
      const depth = dimZ * gridSize;
      const margin = Math.min(10, halfWidth / 2, depth / 4);
      camera.position.x = THREE.MathUtils.clamp(
        camera.position.x,
        -halfWidth + margin,
        halfWidth - margin,
      );
      camera.position.z = THREE.MathUtils.clamp(
        camera.position.z,
        -depth + margin,
        -margin,
      );
      camera.position.y = eyeHeight;
    }

    function restore() {
      walking = false;
      activeRef.current = false;
      keys.clear();
      if (saved) {
        camera.position.copy(saved.position);
        camera.quaternion.copy(saved.quaternion);
        orbit.target.copy(saved.target);
        orbit.enabled = saved.enabled;
        saved = null;
        camera.updateMatrixWorld(true);
      }
      if (!disposed) setActive(false);
    }

    function onLockChange() {
      if (doc.pointerLockElement !== canvas) {
        restore();
        return;
      }
      if (!pending || disposed) {
        doc.exitPointerLock();
        return;
      }
      pending = false;
      saved = {
        position: camera.position.clone(),
        quaternion: camera.quaternion.clone(),
        target: orbit.target.clone(),
        enabled: orbit.enabled,
      };
      orbit.enabled = false;
      walking = true;
      activeRef.current = true;
      keys.clear();
      view.setFromQuaternion(camera.quaternion, "YXZ");
      view.x = 0;
      view.z = 0;
      camera.quaternion.setFromEuler(view);
      clampToFloor();
      camera.updateMatrixWorld(true);
      lastTime = win.performance.now();
      setActive(true);
      setError("");
    }

    function onLockError() {
      pending = false;
      if (!disposed)
        setError(
          "Nie udało się przejąć myszy. Kliknij ponownie „Widok pierwszoosobowy”.",
        );
    }

    function enter() {
      if (walking || pending) return;
      if (!canvas.requestPointerLock) {
        setError(
          "Ta przeglądarka nie obsługuje przejmowania myszy (Pointer Lock).",
        );
        return;
      }
      pending = true;
      setError("");
      try {
        const result = canvas.requestPointerLock();
        result?.catch?.(onLockError);
      } catch {
        onLockError();
      }
    }
    enterRef.current = enter;

    const movementCodes = new Set([
      "KeyW",
      "KeyA",
      "KeyS",
      "KeyD",
      "ShiftLeft",
      "ShiftRight",
    ]);
    function onKeyDown(event) {
      if (!walking || !movementCodes.has(event.code)) return;
      event.preventDefault();
      keys.add(event.code);
    }
    function onKeyUp(event) {
      keys.delete(event.code);
    }
    function onMouseMove(event) {
      if (!walking || doc.pointerLockElement !== canvas) return;
      view.y -= event.movementX * mouseSensitivity;
      view.x = THREE.MathUtils.clamp(
        view.x - event.movementY * mouseSensitivity,
        -Math.PI / 2 + 0.01,
        Math.PI / 2 - 0.01,
      );
      camera.quaternion.setFromEuler(view);
      camera.updateMatrixWorld(true);
    }
    function release() {
      keys.clear();
      pending = false;
      if (doc.pointerLockElement === canvas) doc.exitPointerLock();
      restore();
    }
    function onVisibility() {
      if (doc.hidden) release();
    }

    function animate(now) {
      const dt =
        lastTime === undefined ? 0 : Math.min((now - lastTime) / 1000, 0.05);
      lastTime = now;
      if (walking) {
        // Ruch po X/Z, niezależny od pochylenia głowy.
        forward.set(-Math.sin(view.y), 0, -Math.cos(view.y));
        right.set(Math.cos(view.y), 0, -Math.sin(view.y));
        const longitudinal =
          Number(keys.has("KeyW")) - Number(keys.has("KeyS"));
        const lateral = Number(keys.has("KeyD")) - Number(keys.has("KeyA"));
        motion
          .copy(forward)
          .multiplyScalar(longitudinal)
          .addScaledVector(right, lateral);
        if (motion.lengthSq() > 0) {
          const sprint = keys.has("ShiftLeft") || keys.has("ShiftRight");
          camera.position.addScaledVector(
            motion.normalize(),
            moveSpeed * (sprint ? 2 : 1) * dt,
          );
          clampToFloor();
          camera.updateMatrixWorld(true);
        }
      }
      frame = win.requestAnimationFrame(animate);
    }

    doc.addEventListener("pointerlockchange", onLockChange);
    doc.addEventListener("pointerlockerror", onLockError);
    doc.addEventListener("mousemove", onMouseMove);
    win.addEventListener("keydown", onKeyDown);
    win.addEventListener("keyup", onKeyUp);
    win.addEventListener("blur", release);
    doc.addEventListener("visibilitychange", onVisibility);
    frame = win.requestAnimationFrame(animate);

    return () => {
      disposed = true;
      release();
      win.cancelAnimationFrame(frame);
      doc.removeEventListener("pointerlockchange", onLockChange);
      doc.removeEventListener("pointerlockerror", onLockError);
      doc.removeEventListener("mousemove", onMouseMove);
      win.removeEventListener("keydown", onKeyDown);
      win.removeEventListener("keyup", onKeyUp);
      win.removeEventListener("blur", release);
      doc.removeEventListener("visibilitychange", onVisibility);
      if (enterRef.current === enter) enterRef.current = null;
    };
  }, [
    cameraRef,
    rendererRef,
    controlsRef,
    boardSizeRef,
    activeRef,
    scenarioKey,
    gridSize,
    eyeHeight,
    moveSpeed,
    mouseSensitivity,
  ]);

  return (
    <>
      <Box>
        {!active ? (
          // <Button variant="contained" onClick={() => enterRef.current?.()}>
          //   Widok pierwszoosobowy
          // </Button>
          <IconButton
            onClick={() => enterRef.current?.()}
            sx={{ position: "absolute", bottom: 20, right: 70 }}
          >
            <DirectionsRunIcon />
          </IconButton>
        ) : (
          <Typography
            variant="body2"
            sx={{
              position: "absolute",
              bottom: 20,
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            WASD — chodzenie · Mysz — rozglądanie · Shift — szybciej · Esc —
            powrót
          </Typography>
        )}
        {error && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
      </Box>
      {active && (
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            pointerEvents: "none",
            zIndex: 20,
            color: "white",
            textShadow: "0 0 3px black",
            fontSize: 24,
          }}
        >
          +
        </Box>
      )}
    </>
  );
}
